import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Target, RefreshCcw, BarChart3, CheckCircle, ArrowRight, Phone, Users, TrendingUp, MessageSquare, PhoneCall, Orbit, Brain } from 'lucide-react';

export default function Services() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  const services = [
    {
      id: 'lead-generation',
      icon: Target,
      illustrationIcon: PhoneCall,
      title: 'Lead Generation',
      subtitle: 'Prospection téléphonique',
      description: 'Acquisition de prospects qualifiés par téléphone. Identification et contact de vos clients potentiels pour générer des opportunités commerciales concrètes.',
      features: [
        'Qualification de fichiers prospects',
        'Segmentation de marchés cibles',
        'Scripts d\'appels personnalisés',
        'Campagnes de prospection téléphonique',
        'Prise de rendez-vous qualifiés',
        'Reporting et suivi des KPIs',
      ],
    },
    {
      id: 'sales-360',
      icon: RefreshCcw,
      illustrationIcon: Orbit,
      title: 'Sales 360',
      subtitle: 'Cycle de vente complet',
      description: 'Externalisation complète de votre force de vente. De la prospection à la signature, prise en charge de l\'intégralité de votre processus commercial pour maximiser vos résultats.',
      features: [
        'Génération de leads et qualification',
        'Prise de rendez-vous par téléphone et mail',
        'Présentation et démonstration produit',
        'Négociation commerciale',
        'Closing et signature',
      ],
    },
    {
      id: 'consulting',
      icon: BarChart3,
      illustrationIcon: Brain,
      title: 'Consulting Sales',
      subtitle: 'Stratégie & Formation',
      description: 'Audit et optimisation de vos processus commerciaux. Une expertise terrain pour vous accompagner dans la structuration et l\'amélioration de vos performances de vente.',
      features: [
        'Audit de votre organisation commerciale',
        'Définition de la stratégie de vente',
        'Coaching des équipes sales',
        'Formation aux techniques de vente',
        'Mise en place de CRM et outils',
        'Optimisation des processus',
      ],
    },
  ];

  const benefits = [
    { icon: Users, title: 'Interlocuteur unique', description: 'Un expert dédié à votre réussite' },
    { icon: Target, title: 'Sur mesure', description: 'Solutions adaptées à vos besoins' },
    { icon: TrendingUp, title: 'ROI mesurable', description: 'Résultats concrets et traçables' },
    { icon: MessageSquare, title: 'Communication fluide', description: 'Reporting régulier et transparent' },
  ];

  return (
    <div className="min-h-screen bg-white pt-24" data-testid="services-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-[#ECFDF5]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <span className="text-[#10B981] text-sm font-semibold uppercase tracking-wider mb-4 block">Services proposés</span>
            <h1 className="text-4xl md:text-5xl font-bold text-[#1A1A1A] leading-tight mb-6" data-testid="services-title">
              Des solutions complètes pour votre <span className="text-[#10B981]">croissance commerciale</span>
            </h1>
            <p className="text-lg text-[#1E3A5F] font-semibold leading-relaxed">
              Découvrez une gamme de services conçus pour accélérer votre développement et transformer vos opportunités en succès durables.
            </p>
          </div>
        </div>
      </section>

      {/* Services Detail Section */}
      <section className="py-20" data-testid="services-detail-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="space-y-24">
            {services.map((service, index) => (
              <div
                key={service.id}
                id={service.id}
                data-testid={`service-detail-${service.id}`}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                {/* Content */}
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="w-14 h-14 rounded-xl bg-[#E8F5F3] flex items-center justify-center mb-6">
                    <service.icon size={28} className="text-[#10B981]" />
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-2">{service.title}</h2>
                  <p className="text-[#10B981] font-medium text-sm mb-6">{service.subtitle}</p>
                  <p className="text-[#1E3A5F] font-semibold text-lg leading-relaxed mb-8">{service.description}</p>

                  {/* Features List */}
                  <ul className="space-y-4 mb-8">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-3">
                        <CheckCircle size={22} className="text-[#10B981] flex-shrink-0" />
                        <span className="text-[#1A1A1A] font-medium">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Link
                    to="/contact"
                    data-testid={`service-cta-${service.id}`}
                    className="inline-flex items-center gap-2 text-[#10B981] font-semibold hover:gap-4 transition-all duration-300"
                  >
                    Demander un devis
                    <ArrowRight size={18} />
                  </Link>
                </div>

                {/* Visual Card */}
                <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <div className="bg-[#10B981] rounded-3xl p-8 md:p-12 flex flex-col items-center justify-center min-h-[280px] shadow-xl">
                    {/* Icon Illustration */}
                    <div className="text-white mb-6">
                      <service.illustrationIcon size={100} strokeWidth={1.5} />
                    </div>

                    {/* Label */}
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-10 h-px bg-white" />
                      <span className="text-white font-semibold text-xs uppercase tracking-wider">
                        Service 0{index + 1}
                      </span>
                      <div className="w-10 h-px bg-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-white text-center">{service.title}</h3>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-[#ECFDF5]" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-[#10B981] text-sm font-semibold uppercase tracking-wider mb-4 block">Les avantages</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A1A]">
              Les avantages de travailler avec un partenaire dédié à votre croissance
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                data-testid={`benefit-card-${index}`}
                className="bg-white border border-gray-100 rounded-2xl p-6 shadow-lg"
              >
                <div className="w-12 h-12 rounded-xl bg-[#10B981] flex items-center justify-center mb-4">
                  <benefit.icon size={24} className="text-white" />
                </div>
                <h3 className="text-lg font-semibold text-[#1A1A1A] mb-2">{benefit.title}</h3>
                <p className="text-[#1E3A5F] font-semibold text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#10B981]" data-testid="services-cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Prêt à booster vos ventes ?
          </h2>
          <p className="text-white/80 text-lg mb-10 max-w-xl mx-auto">
            Échangeons sur vos objectifs et définissons ensemble la meilleure stratégie pour votre développement.
          </p>
          <Link
            to="/contact"
            data-testid="services-final-cta"
            className="inline-flex items-center justify-center gap-3 bg-white text-[#10B981] font-semibold rounded-full px-8 py-4 hover:bg-gray-100 transition-all duration-200"
          >
            <Phone size={20} />
            Demander un devis
          </Link>
        </div>
      </section>
    </div>
  );
}
